import services from "@/config/services";

/**
 * Service per la gestione dei RID SDD Allianz
 * Gestisce le chiamate API per i RID inviati e le notifiche ricevute
 */
export default class RidSddAllianzService {
  constructor(http, eventBus) {
    this.http = http;
    this.eventBus = eventBus;
    this.baseUrl = `${services.ask_pratiche_api.url}/management/rid-sdd-allianz`;
  }

  /**
   * Recupera l'elenco dei RID inviati con filtri opzionali e paginazione
   * @param {Object} filters - Filtri da applicare
   * @param {string} filters.dataFrom - Data inizio (formato YYYY-MM-DD)
   * @param {string} filters.dataTo - Data fine (formato YYYY-MM-DD)
   * @param {string} filters.search - Testo da cercare (codice cliente, POD/PDR, IBAN, titolare)
   * @param {number} page - Numero pagina (default 1)
   * @param {number} perPage - Elementi per pagina (default 10)
   * @returns {Promise<{items: Array, total: number, page: number, lastPage: number}>}
   */
  async getInviati(filters = {}, page = 1, perPage = 10) {
    let items = [];
    let total = 0;
    let lastPage = 1;
    let totaleImporto = 0;

    const url = `${this.baseUrl}/inviati`;

    try {
      const res = await this.http.post(url, {
        filters,
        page,
        per_page: perPage,
      });
      const data = res.data;

      items = data.data || [];
      total = data.meta?.total || items.length;
      lastPage = data.meta?.last_page || 1;
      totaleImporto = data.meta?.totale_importo || 0;
    } catch (err) {
      console.error("Errore nel caricamento dei RID inviati:", err);
      this.eventBus.emit("data-load-error", { message: err, url });
    }

    this.eventBus.emit("data-loaded");
    return {
      items,
      total,
      page,
      lastPage,
      totaleImporto,
    };
  }

  /**
   * Crea il payment (e il record RID) per un inviato specifico
   * @param {number} id - ID del record RID inviato
   * @returns {Promise<Object>} - { success, message, status, fattura, amount, payment_date, record_id }
   */
  async createPayment(id) {
    const url = `${this.baseUrl}/inviati/${id}/payment`;
    const res = await this.http.post(url, {});
    return res.data;
  }

  /**
   * Crea il payment di storno per una notifica/storno ricevuto.
   * Il pagamento risultante avrà importo negativo (rimborso/storno) sulla fattura.
   * @param {number} id - ID del record notifica/storno
   * @returns {Promise<Object>} - { success, message, status, fattura, amount, payment_date, record_id }
   */
  async createNotificaPayment(id) {
    const url = `${this.baseUrl}/notifiche/${id}/payment`;
    const res = await this.http.post(url, {});
    return res.data;
  }

  /**
   * Recupera i valori distinti delle distinte (filename) per il dropdown filtro
   * @param {string|null} dataFrom - Data inizio opzionale (YYYY-MM-DD)
   * @param {string|null} dataTo - Data fine opzionale (YYYY-MM-DD)
   * @returns {Promise<Array<{label: string, value: string}>>}
   */
  async getDistinte(dataFrom = null, dataTo = null) {
    const url = `${this.baseUrl}/inviati/distinte`;
    try {
      const res = await this.http.post(url, { dataFrom, dataTo });
      return res.data?.items || [];
    } catch (err) {
      console.error("Errore nel caricamento delle distinte:", err);
      return [];
    }
  }

  /**
   * Recupera i valori distinti delle distinte (filename) per il dropdown filtro degli storni
   * @param {string|null} dataFrom - Data inizio opzionale (YYYY-MM-DD)
   * @param {string|null} dataTo - Data fine opzionale (YYYY-MM-DD)
   * @returns {Promise<Array<{label: string, value: string}>>}
   */
  async getDistinteNotifiche(dataFrom = null, dataTo = null) {
    const url = `${this.baseUrl}/notifiche/distinte`;
    try {
      const res = await this.http.post(url, { dataFrom, dataTo });
      return res.data?.items || [];
    } catch (err) {
      console.error("Errore nel caricamento delle distinte notifiche:", err);
      return [];
    }
  }

  /**
   * Recupera l'elenco delle notifiche ricevute con filtri opzionali e paginazione
   * @param {Object} filters - Filtri da applicare
   * @param {string} filters.dataFrom - Data inizio (formato YYYY-MM-DD)
   * @param {string} filters.dataTo - Data fine (formato YYYY-MM-DD)
   * @param {string} filters.search - Testo da cercare (codice cliente, POD/PDR, causale)
   * @param {string} filters.filename - Distinta (filename del pain.008 originale) opzionale
   * @param {number} page - Numero pagina (default 1)
   * @param {number} perPage - Elementi per pagina (default 10)
   * @returns {Promise<{items: Array, total: number, page: number, lastPage: number}>}
   */
  async getNotifiche(filters = {}, page = 1, perPage = 10) {
    let items = [];
    let total = 0;
    let lastPage = 1;
    let totaleImporto = 0;

    const url = `${this.baseUrl}/notifiche`;

    try {
      const res = await this.http.post(url, {
        filters,
        page,
        per_page: perPage,
      });
      const data = res.data;

      items = data.data || [];
      total = data.meta?.total || items.length;
      lastPage = data.meta?.last_page || 1;
      totaleImporto = data.meta?.totale_importo || 0;
    } catch (err) {
      console.error("Errore nel caricamento delle notifiche:", err);
      this.eventBus.emit("data-load-error", { message: err, url });
    }

    this.eventBus.emit("data-loaded");
    return {
      items,
      total,
      page,
      lastPage,
      totaleImporto,
    };
  }

  /**
   * Recupera i dati raw della notifica/storno selezionata.
   * @param {number|string} notificaId - ID della notifica da ispezionare
   * @returns {Promise<Object>}
   */
  async getNotificaRaw(notificaId) {
    const url = `${this.baseUrl}/notifiche/${notificaId}/raw`;

    try {
      const res = await this.http.get(url);
      this.eventBus.emit("data-loaded");
      return res.data?.data ?? res.data;
    } catch (err) {
      console.error("Errore nel caricamento dei dati raw della notifica:", err);
      this.eventBus.emit("data-load-error", { message: err, url });
      throw err;
    }
  }

  /**
   * Recupera l'elenco dei file da firmare o firmati
   *
   * @param {Object} filters - Filtri da applicare
   * @param {string} filters.dataFrom - Data inizio
   * @param {string} filters.dataTo - Data fine
   * @param {string} filters.search - Ricerca libera
   * @param {string} filters.status - 'non_firmati' (default), 'firmati', 'all'
   * @param {number} page - Numero pagina
   * @param {number} perPage - Elementi per pagina
   * @returns {Promise<{items: Array, total: number, page: number, lastPage: number}>}
   * 
   * Ogni item nell'array deve contenere:
   * - id: number - ID del file
   * - filename: string - Nome del file XML
   * - createdAt: string - Data creazione (ISO 8601)
   * - importo: number - Importo totale del file RID
   * - status: string - 'non_firmato', 'firmato', 'errore'
   */
  async getFilesFirma(filters = {}, page = 1, perPage = 10) {
    let items = [];
    let total = 0;
    let lastPage = 1;

    // Endpoint inventato
    const url = `${this.baseUrl}/files-firma`;

    try {
      const res = await this.http.post(url, {
        filters,
        page,
        per_page: perPage,
      });
      const data = res.data;

      items = data.data || [];
      total = data.meta?.total || items.length;
      lastPage = data.meta?.last_page || 1;
    } catch (err) {
      console.error("Errore nel caricamento dei file firma:", err);
      this.eventBus.emit("data-load-error", { message: err, url });
    }

    this.eventBus.emit("data-loaded");
    return {
      items,
      total,
      page,
      lastPage,
    };
  }

  /**
   * Elabora (firma) un file - cambia lo stato a 'firmato'
   * @param {number|string} fileId - ID del file da elaborare
   * @returns {Promise<Object>}
   */
  async elaboraFile(fileId) {
    const url = `${this.baseUrl}/files-firma/${fileId}/elabora`;

    try {
      const res = await this.http.post(url);
      this.eventBus.emit("data-loaded");
      return res.data;
    } catch (err) {
      console.error("Errore nell'elaborazione del file:", err);
      this.eventBus.emit("data-load-error", { message: err, url });
      throw err;
    }
  }

  /**
   * Segnala un file con errori - cambia lo stato a 'errore'
   * @param {number|string} fileId - ID del file da segnalare
   * @returns {Promise<Object>}
   */
  async segnalaErroreFile(fileId) {
    const url = `${this.baseUrl}/files-firma/${fileId}/errore`;

    try {
      const res = await this.http.post(url);
      this.eventBus.emit("data-loaded");
      return res.data;
    } catch (err) {
      console.error("Errore nella segnalazione del file:", err);
      this.eventBus.emit("data-load-error", { message: err, url });
      throw err;
    }
  }

  /**
   * Segnala un IBAN errato associato a una fattura
   * @param {string} numeroFattura - Numero fattura (es. A25006247)
   * @returns {Promise<Object>}
   */
  async segnalaIbanErrato(numeroFattura) {
    const url = `${this.baseUrl}/fattura/iban-errato`;

    try {
      const res = await this.http.post(url, { numero_fattura: numeroFattura });
      this.eventBus.emit("data-loaded");
      return res.data;
    } catch (err) {
      console.error("Errore nella segnalazione IBAN errato:", err);
      throw err;
    }
  }
}
